import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {IEmailAttachment} from '@/services/helpdeskEmailAttachments';
import {RootState} from '../RootState';
import {settings} from '@/plugins/settings';
import {helpdeskAxios} from '@/plugins/helpdeskAxios';
import responseHandler from '@/plugins/errorHandling';

export interface IHelpdeskEmail {
  id: number;
  gmail_account_id: number;
  gmail_message_id: string;
  thread_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  body: string;
  snippet: string;
  status: 'pending' | 'read' | 'spam' | 'ignored' | 'archived' | 'ticket_assigned' | 'ticket_resolved';
  received_at: string;
  created_at: string;
  ticket_id?: number | null;
  gmail_account?: { id: number; email: string } | null;
  // Metadata only; null for emails imported before attachments were tracked.
  attachments?: IEmailAttachment[] | null;
}

interface InboxState {
  emails: IHelpdeskEmail[];
  selectedEmail: IHelpdeskEmail | null;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  errors: any;
  // Guards against out-of-order responses: only the latest request may update the view.
  emailsRequestSeq: number;
  requestedEmailId: number | null;
}

const state: InboxState = {
  emails: [],
  selectedEmail: null,
  currentPage: 1,
  lastPage: 1,
  total: 0,
  perPage: 30,
  errors: {},
  emailsRequestSeq: 0,
  requestedEmailId: null,
};

const getters: GetterTree<InboxState, RootState> = {
  emails:        (state) => state.emails,
  selectedEmail: (state) => state.selectedEmail,
  currentPage:   (state) => state.currentPage,
  lastPage:      (state) => state.lastPage,
  total:         (state) => state.total,
  perPage:       (state) => state.perPage,
};

const mutations: MutationTree<InboxState> = {
  errors(state, payload: any) { state.errors = payload; },
  emailsRequested(state, seq: number) { state.emailsRequestSeq = seq; },
  emailsLoaded(state, payload: any) {
    // A slower response from a previous search/filter/page must not replace the current list.
    if (payload._seq !== undefined && payload._seq !== state.emailsRequestSeq) return;
    state.emails      = payload.data;
    state.currentPage = payload.current_page;
    state.lastPage    = payload.last_page;
    state.total       = payload.total;
    state.perPage     = payload.per_page;
  },
  emailRequested(state, id: number) { state.requestedEmailId = id; },
  emailLoaded(state, payload: IHelpdeskEmail) {
    // Clicking A then B quickly: A's late response must not replace B.
    if (state.requestedEmailId !== null && payload.id !== state.requestedEmailId) return;
    state.selectedEmail = payload;
  },
  emailStatusUpdated(state, payload: IHelpdeskEmail) {
    // markAsRead of an email you already left must not pull the view back to it.
    if (state.selectedEmail && state.selectedEmail.id === payload.id) state.selectedEmail = payload;
    const idx = state.emails.findIndex(e => e.id === payload.id);
    if (idx !== -1) state.emails.splice(idx, 1, payload);
  },
  emailReplied(_state, _payload: any) {},
  emailForwarded(_state, _payload: any) {},
  emailLinkedToTicket(state, payload: IHelpdeskEmail) {
    if (state.selectedEmail && state.selectedEmail.id === payload.id) state.selectedEmail = payload;
    const idx = state.emails.findIndex(e => e.id === payload.id);
    if (idx !== -1) state.emails.splice(idx, 1, payload);
  },
};

let emailsRequestCounter = 0;

const actions: ActionTree<InboxState, RootState> = {
  fetchEmails: ({commit}, params: {statuses?: string[]; account_id?: number; page?: number; per_page?: number; search?: string} | undefined) => {
    const query = new URLSearchParams();
    if (params && params.statuses && params.statuses.length) query.set('status', params.statuses.join(','));
    if (params && params.account_id)  query.set('account_id', String(params.account_id));
    if (params && params.page)        query.set('page',       String(params.page));
    if (params && params.per_page)    query.set('per_page',   String(params.per_page));
    if (params && params.search)      query.set('search',     params.search);
    const qs = query.toString() ? '?' + query.toString() : '';
    const seq = ++emailsRequestCounter;
    commit('emailsRequested', seq);
    return responseHandler.handle(
      () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox' + qs, method: 'GET'})
        .then((res: any) => { if (res.data && res.data.data) res.data.data._seq = seq; return res; }),
      commit, 'emailsLoaded',
    );
  },
  fetchEmail: ({commit}, id: number) => {
    commit('emailRequested', id);
    return responseHandler.handle(
      () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id, method: 'GET'}),
      commit, 'emailLoaded',
    );
  },
  updateEmailStatus: ({commit}, {id, status}: {id: number; status: string}) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id + '/status', method: 'PATCH', data: {status}}),
    commit, 'emailStatusUpdated',
  ),
  // The backend puts it back where it was: its ticket status if it has one, otherwise read.
  unarchiveEmail: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id + '/unarchive', method: 'PATCH'}),
    commit, 'emailStatusUpdated',
  ),
  markAsRead: ({commit}, id: number) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id + '/read', method: 'PATCH'}),
    commit, 'emailStatusUpdated',
  ),
  // `to` replaces the original sender (e.g. answering the customer of a forwarded email);
  // `include_history` quotes the email being answered, for recipients who never saw the thread.
  replyEmail: ({commit}, {id, body, sent_by, subject, to, cc, bcc, include_history}: {id: number; body: string; sent_by?: string; subject?: string; to?: string[]; cc?: string[]; bcc?: string[]; include_history?: boolean}) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id + '/reply', method: 'POST', data: {body, sent_by, subject, to, cc, bcc, include_history}}),
    commit, 'emailReplied', null, false,
  ),
  forwardEmail: ({commit}, {id, to_email, sent_by, cc, bcc}: {id: number; to_email: string; sent_by?: string; cc?: string[]; bcc?: string[]}) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + id + '/forward', method: 'POST', data: {to_email, sent_by, cc, bcc}}),
    commit, 'emailForwarded', null, false,
  ),
  linkEmailToTicket: ({commit}, {emailId, ticketId}: {emailId: number; ticketId: number}) => responseHandler.handle(
    () => helpdeskAxios({url: settings.helpdeskEndpoint + 'v1/inbox/' + emailId + '/link-ticket', method: 'POST', data: {ticket_id: ticketId}}),
    commit, 'emailLinkedToTicket', null, false,
  ),
};

export const helpdeskInboxModule: Module<InboxState, RootState> = {
  namespaced: true, state, getters, mutations, actions,
};
